/**
 * Aktionsskript: legt ab einem Mindestwert einen Gratis-Artikel in den Warenkorb und wendet den
 * passenden Rabattcode an. Übernimmt das Verhalten von GCC (AutoApplyDiscountCode), nicht den Code.
 * Bisher im Einsatz: Tag des Kaffees 2026 (Cauca 250 g gratis ab 69 €, Code CAUCA) und
 * Black Week 2026 (eine von drei Sorten, 250 g gratis ab 59 €, Code BLACKWEEK26).
 *
 * Die Sorte wechselt der Kunde in der Gratis-Zeile über den Knopf „Kaffee wechseln“. Die Sorten
 * dafür stehen in snippets/mk-cart-gift-option.liquid und müssen zu giftVariantIds passen.
 *
 * Zwischen zwei Aktionen ist die Datei nicht eingebunden. Für die nächste Aktion CAMPAIGN an den
 * neuen Rabatt anpassen und in snippets/scripts.liquid nach mk-age-gate.js wieder einbinden, als
 * [MK vendor-mod] mit Eintrag im Dateikopf:
 *   <script src="{{ 'mk-gift-campaign.js' | asset_url }}" type="module" fetchpriority="low"></script>
 * Test: tools/playwright/gift-campaign-verify.mjs
 *
 * Vor dem Start testen: im Admin den Start des Rabatts vorübergehend auf heute stellen und den Shop
 * mit ?mk-campaign-test=1 aufrufen. Dann gilt der Start im Skript nicht, bis der Tab geschlossen
 * wird. Das Ende gilt weiter. Danach den Start im Admin zurückstellen. Ohne den Parameter bleibt
 * das Skript vor dem Start stumm. Solange der Start vorgezogen ist, greift der Code aber bei allen,
 * die ihn eingeben.
 *
 * Auslöser ist jedes cart:update, auch eine Mengenänderung im Warenkorb. Beim Seitenaufruf
 * läuft nichts. Liegt schon eine der Gratis-Varianten im Warenkorb, kommt nur der Code dazu und
 * Shopify macht diese Zeile gratis. Entfernt der Kunde den Gratis-Artikel, legt ihn das nächste
 * cart:update wieder hinein.
 *
 * Nichts passiert
 *   - vor dem Start ohne Test-Parameter (siehe oben) und nach dem Ende,
 *   - mit einem ausgeschlossenen Produkt im Warenkorb (2026 der Adventskalender, weil ADVENT10
 *     mit keinem anderen Rabatt kombiniert),
 *   - wenn schon ein Code greift. Der Aktionscode kombiniert mit keinem Rabatt, ein fremder Code
 *     bleibt,
 *   - unter dem Mindestwert. Gutscheine zählen nicht, so wie in der Kollektion
 *     „Rabattfähig (ohne Gutscheine)“, auf die der Mindestwert des Rabatts zählt.
 *
 * Greift der Code nach dem Anwenden trotzdem nicht, nimmt das Skript Artikel und Code wieder
 * heraus. Fällt der Warenkorb später unter den Mindestwert, bleibt der Artikel liegen und kostet
 * wieder.
 *
 * Die Werte stehen fest im Code wie bei GCC und spiegeln den Rabatt im Admin.
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const CAMPAIGN = {
  code: 'BLACKWEEK26',
  // 23.11.2026 00:00 MEZ bis zum Ende des Rabattcodes am 29.11. um 23:59 MEZ. Ändern sich Start
  // oder Ende im Admin, müssen die Werte mit.
  start: Date.parse('2026-11-23T00:00:00+01:00'),
  end: Date.parse('2026-11-29T23:59:00+01:00'),
  // Mindestwert des Rabatts in Cent
  threshold: 5900,
  // Pacha Mama 250 g / Bohne
  defaultVariantId: 57739745493327,
  // Alle 250-g-Varianten, die der Rabatt gratis macht: Pacha Mama, Nueva Esperanza und Nossa
  // Senhora mit je acht Mahlgraden
  giftVariantIds: [
    57739745493327, 57743813214543, 57743813247311, 57743813280079, 57743813312847, 58354353471823, 58354353504591,
    58354353537359, 57739745689935, 57743813542223, 57743813574991, 57743813607759, 57743813640527, 58354354389327,
    58354354422095, 58354354454863, 57739746476367, 57743819800911, 57743819833679, 57743819866447, 57743819899215,
    58354361762127, 58354361794895, 58354361827663,
  ],
  // Kaffee-Adventskalender
  excludedProductIds: [16261723980111],
  // Produkttypen außerhalb der Kollektion „Rabattfähig (ohne Gutscheine)“
  excludedProductTypes: ['Gutschein', 'Geschenkgutscheine'],
};

const SOURCE = 'mk-gift-campaign';
// Hebt den Start auf, siehe Dateikopf
const TEST_PARAM = 'mk-campaign-test';
const testMode = readTestMode();

let running = false;
let queued = false;

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.sourceId === SOURCE) return;

  const now = Date.now();
  if ((now < CAMPAIGN.start && !testMode) || now > CAMPAIGN.end) return;

  run();
});

/**
 * Steht der Test-Parameter in der Adresse, gilt der Start bis zum Schließen des Tabs nicht. Ohne
 * Zugriff auf den Speicher gilt er nur auf der aufgerufenen Seite.
 */
function readTestMode() {
  const requested = new URLSearchParams(location.search).has(TEST_PARAM);
  try {
    if (requested) sessionStorage.setItem(TEST_PARAM, '1');
    return sessionStorage.getItem(TEST_PARAM) === '1';
  } catch {
    return requested;
  }
}

/** Ereignisse während eines Laufs lösen genau einen weiteren Lauf aus. */
async function run() {
  if (running) {
    queued = true;
    return;
  }

  running = true;
  do {
    queued = false;
    try {
      await applyCampaign();
    } catch (error) {
      console.error(error);
    }
  } while (queued);
  running = false;
}

async function applyCampaign() {
  const cart = await cartRequest(`${Theme.routes.cart_url}.js`);
  if (!cart || !qualifies(cart)) return;

  const codes = cart.discount_codes.map((entry) => entry.code);
  const hasGift = cart.items.some((item) => CAMPAIGN.giftVariantIds.includes(item.variant_id));

  /** @type {{ discount: string, updates?: Record<string, number> }} */
  const body = { discount: [...codes.filter((code) => !isCampaignCode(code)), CAMPAIGN.code].join(',') };
  // Nur ohne Gratis-Variante im Warenkorb: update.js setzt eine vorhandene Zeile derselben Variante
  // auf die neue Menge, statt zu addieren.
  if (!hasGift) body.updates = { [CAMPAIGN.defaultVariantId]: 1 };

  // Bei einer Fehlerantwort hat Shopify nichts geändert, es gibt nichts zurückzubauen.
  const state = await cartRequest(Theme.routes.cart_update_url, body);
  if (!state) return;

  if (state.discount_codes.some((entry) => entry.applicable && isCampaignCode(entry.code))) {
    announce(state);
    return;
  }

  // Der Code greift nicht: Codes wiederherstellen und den eben gelegten Gratis-Artikel herausnehmen.
  /** @type {{ discount: string, updates?: Record<string, number> }} */
  const rollback = { discount: codes.join(',') };
  const added = hasGift ? null : state.items.find((item) => item.variant_id === CAMPAIGN.defaultVariantId);
  if (added) rollback.updates = { [added.key]: 0 };
  announce((await cartRequest(Theme.routes.cart_update_url, rollback)) ?? state);
}

/**
 * Soll das Skript eingreifen?
 * @param {{ items: Array<{ product_id: number, product_type: string, final_line_price: number }>, discount_codes: Array<{ applicable: boolean }> }} cart
 */
function qualifies(cart) {
  if (cart.items.some((item) => CAMPAIGN.excludedProductIds.includes(item.product_id))) return false;
  if (cart.discount_codes.some((entry) => entry.applicable)) return false;

  const subtotal = cart.items
    .filter((item) => !CAMPAIGN.excludedProductTypes.includes(item.product_type))
    .reduce((sum, item) => sum + item.final_line_price, 0);
  return subtotal >= CAMPAIGN.threshold;
}

/** @param {string} code */
function isCampaignCode(code) {
  return code.toUpperCase() === CAMPAIGN.code;
}

/**
 * Liest den Warenkorb oder ändert ihn. Bei einer Fehlerantwort gibt es null zurück.
 * @param {string} url
 * @param {object} [body]
 */
async function cartRequest(url, body) {
  const response = await fetch(url, body ? fetchConfig('json', { body: JSON.stringify(body) }) : undefined);
  if (!response.ok) {
    console.error(`${SOURCE}: ${url} antwortet ${response.status}`);
    return null;
  }
  return response.json();
}

/**
 * Meldet den neuen Warenkorb. Die Warenkorb-Ansichten zeichnen sich daraufhin neu, der Zähler
 * übernimmt die Menge.
 * @param {{ item_count: number }} cart
 */
function announce(cart) {
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { itemCount: cart.item_count, source: SOURCE }));
}
